import { makeAuthenticatedRequest } from './authService';

export interface Drawing {
  id: number;
  user_id: number;
  canvas_data: string; // Base64 encoded canvas image
  mime_type: string;
  brush_size: number;
  eraser_size: number;
  background_image?: string | null; // Base64 encoded AI-generated background image
  background_opacity?: number | null; // Opacity of background image (0.0-1.0)
  created_at: string;
  updated_at: string;
}

export interface SaveDrawingRequest {
  canvas_data: string;
  mime_type?: string;
  brush_size?: number;
  eraser_size?: number;
  background_image?: string | null;
  background_opacity?: number | null;
}

/**
 * Get user's current drawing
 */
export const getDrawing = async (userId: number): Promise<Drawing | null> => {
  try {
    const response = await makeAuthenticatedRequest('/drawings', {
      method: 'GET'
    });
    
    if (response.success && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error: any) {
    // If 404, return null (no drawing exists)
    if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
      return null;
    }
    throw error;
  }
};

/**
 * Save or update user's drawing (upsert)
 */
export const saveDrawing = async (
  userId: number,
  canvasData: string,
  brushSize: number,
  eraserSize: number,
  backgroundImage?: string | null,
  backgroundOpacity?: number | null
): Promise<Drawing> => {
  const response = await makeAuthenticatedRequest('/drawings', {
    method: 'POST',
    body: JSON.stringify({
      canvas_data: canvasData,
      mime_type: 'image/png',
      brush_size: brushSize,
      eraser_size: eraserSize,
      background_image: backgroundImage,
      background_opacity: backgroundOpacity
    })
  });
  
  return response.data;
};

/**
 * Update user's drawing (upsert)
 */
export const updateDrawing = async (
  userId: number,
  canvasData: string,
  brushSize?: number,
  eraserSize?: number,
  backgroundImage?: string | null,
  backgroundOpacity?: number | null
): Promise<Drawing> => {
  const response = await makeAuthenticatedRequest('/drawings', {
    method: 'PUT',
    body: JSON.stringify({
      canvas_data: canvasData,
      mime_type: 'image/png',
      brush_size: brushSize,
      eraser_size: eraserSize,
      background_image: backgroundImage,
      background_opacity: backgroundOpacity
    })
  });
  
  return response.data;
};

/**
 * Get all user's drawings
 */
export const getAllDrawings = async (userId: number): Promise<Drawing[]> => {
  try {
    const response = await makeAuthenticatedRequest('/drawings/all', {
      method: 'GET'
    });
    
    if (response.success && response.data) {
      return Array.isArray(response.data) ? response.data : [];
    }
    
    return [];
  } catch (error: any) {
    // If 404 or error, return empty array
    if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
      return [];
    }
    throw error;
  }
};

/**
 * Get a specific drawing by ID
 */
export const getDrawingById = async (userId: number, drawingId: number): Promise<Drawing | null> => {
  try {
    const response = await makeAuthenticatedRequest(`/drawings/${drawingId}`, {
      method: 'GET'
    });
    
    if (response.success && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error: any) {
    // If 404, return null (drawing doesn't exist)
    if (error.message && (error.message.includes('404') || error.message.includes('not found'))) {
      return null;
    }
    throw error;
  }
};

/**
 * Delete a specific drawing by ID
 */
export const deleteDrawingById = async (userId: number, drawingId: number): Promise<void> => {
  await makeAuthenticatedRequest(`/drawings/${drawingId}`, {
    method: 'DELETE'
  });
};

/**
 * Delete user's most recent drawing (for backward compatibility)
 */
export const deleteDrawing = async (userId: number): Promise<void> => {
  await makeAuthenticatedRequest('/drawings', {
    method: 'DELETE'
  });
};

